import React from "react";
import { motion } from "framer-motion";

/**
 * ProcessSteps — Apple-style minimal "How it works" section.
 * 3 (or N) numbered steps in a clean horizontal grid with generous
 * whitespace. No sticky scroll, no fancy cards — just type.
 *
 * @param {object} props
 * @param {{ num: string, title: string, body: string }[]} props.steps
 */
export default function ProcessSteps({ steps = DEFAULT_STEPS, className = "" }) {
  return (
    <section
      className={`relative ${className}`}
      data-testid="process-steps"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-20 md:mb-28"
        >
          <p className="overline mb-6 text-gold-bright">How it works</p>
          <h2 className="section-heading mx-auto max-w-3xl">
            Three steps.{" "}
            <em
              className="italic gold-text"
              style={{ fontFamily: "'Instrument Serif', 'Fraunces', Georgia, serif" }}
            >
              That's the whole thing.
            </em>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
          {steps.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.9,
                delay: i * 0.12,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="relative"
              data-testid={`process-step-${i}`}
            >
              {/* Subtle divider on md+ between items */}
              {i > 0 && (
                <div
                  className="hidden md:block absolute -left-6 md:-left-8 top-3 bottom-3 w-px"
                  style={{
                    background:
                      "linear-gradient(to bottom, transparent, rgba(138,138,138,0.25), transparent)",
                  }}
                />
              )}

              <div className="flex items-baseline gap-3 mb-6">
                <span
                  className="font-mono text-sm tracking-[0.25em] text-gold"
                  style={{ fontWeight: 500 }}
                >
                  {s.num}
                </span>
                <div
                  className="flex-1 h-px"
                  style={{ background: "rgba(138,138,138,0.15)" }}
                />
              </div>

              <h3
                className="font-semibold tracking-[-0.02em] text-foreground mb-4"
                style={{ fontSize: "clamp(1.4rem, 2.4vw, 1.9rem)", lineHeight: 1.1 }}
              >
                {s.title}
              </h3>

              <p
                className="leading-relaxed max-w-sm"
                style={{ fontSize: "16px", color: "#b8b8c0" }}
              >
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const DEFAULT_STEPS = [
  {
    num: "01",
    title: "Book in 90 seconds.",
    body: "Pick a date, tell us about the car, see your estimate. No phone tag, no back-and-forth.",
  },
  {
    num: "02",
    title: "We come to you.",
    body: "We arrive at your driveway with everything we need. You just leave the car out and we'll handle the rest.",
  },
  {
    num: "03",
    title: "Showroom finish.",
    body: "Two to three hours later your car looks like the day you bought it. Pay when it's done.",
  },
];
